import React, { useState } from 'react';
import { createPortfolioNew, getGuru } from '../composables/fetchAPI';
import toast from 'react-hot-toast';

type Guru = {
  id: string;
  name: string;
  description: string;
  portfolios: Portfolio[];
};
type Portfolio = {
  id: string;
  name: string;
  description: string;
  path: string;
};

const GuruModal: React.FC<any> = ({ open, onClose, onCreateGuru, onCreatePortfolio, onDeleteGuru, gurus, setGurus, onDeletePortfolio }) => {
  const [selectedGuru, setSelectedGuru] = useState<Guru | null>(null);
  const [newGuruName, setNewGuruName] = useState('');
  const [newPortfolioName, setNewPortfolioName] = useState(''); // State สำหรับชื่อพอร์ตใหม่
  const [loadingAddGuru, setLoadingAddGuru] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPortfolioInput, setShowPortfolioInput] = useState(false); // State สำหรับควบคุมการแสดง TextInput

  const handleAddGuru = async () => {
    if (!newGuruName.trim()) {
      alert('กรุณากรอกชื่อกูรู');
      return;
    }

    setLoadingAddGuru(true);
    try {
      await onCreateGuru({ name: newGuruName });
      setNewGuruName('');
      toast.success('เพิ่มกูรูสำเร็จ');
      const updatedGurus = await getGuru();
      setGurus(updatedGurus);
    } catch (error) {
      console.error('Error creating guru:', error);
      toast.error('ไม่สามารถเพิ่มกูรูได้');
    } finally {
      setLoadingAddGuru(false);
    }
  };

  const handleSelectGuru = (guru: Guru) => {
    if (selectedGuru?.id === guru.id) {
      setSelectedGuru(null);
    } else {
      setSelectedGuru(guru);
    }
  };

  const handleDeletePortfolio = (guruId: string, portfolioId: string, portfolioName: string) => {
    toast((t) => (
      <div>
        <p>คุณต้องการลบพอร์ต {portfolioName} ใช่ไหม?</p>
        <div className="flex justify-end mt-2">
          <button
            className="bg-red-500 text-white px-4 py-2 rounded mr-2"
            onClick={async () => {
              toast.dismiss(t.id); // ปิด toast
              try {
                await onDeletePortfolio(guruId, portfolioId); // ลบพอร์ต
                toast.success(`ลบพอร์ต ${portfolioName} สำเร็จ`);
                const updatedGurus = await getGuru(); // โหลดข้อมูล Guru ใหม่
                setGurus(updatedGurus); // อัปเดตลิสต์ Guru
                setSelectedGuru(updatedGurus.find((guru: Guru) => guru.id === guruId) || null); // อัปเดต Guru ที่เลือก
              } catch (error) {
                console.error('Error deleting portfolio:', error);
                toast.error('ไม่สามารถลบพอร์ตได้');
              }
            }}
          >
            ใช่
          </button>
          <button
            className="bg-gray-500 text-white px-4 py-2 rounded"
            onClick={() => toast.dismiss(t.id)} // ปิด toast
          >
            ยกเลิก
          </button>
        </div>
      </div>
    ));
  };

  const handleAddPortfolio = async () => {
    if (!newPortfolioName.trim()) {
      alert('กรุณากรอกชื่อพอร์ต');
      return;
    }

    setLoading(true);
    try {
      if (selectedGuru) {
        await onCreatePortfolio(selectedGuru.id, newPortfolioName);
     
        const updatedGurus = await getGuru();
        setGurus(updatedGurus);
        setSelectedGuru(updatedGurus.find((guru: Guru) => guru.id === selectedGuru.id) || null);
        setNewPortfolioName(''); // ล้างช่องกรอกชื่อพอร์ต
        setShowPortfolioInput(false); // ซ่อนช่องกรอกชื่อพอร์ตหลังเพิ่มสำเร็จ
      }
    } catch (error) {
      console.error('Error creating portfolio:', error);
      toast.error('ไม่สามารถเพิ่มพอร์ตได้');
    } finally {
      setLoading(false);
    }
  };



  const handleDeleteGuru = (guru: Guru) => {
    toast((t) => (
      <div>
        <p>คุณต้องการลบ {guru.name} ใช่ไหม?</p>
        <div className="flex justify-end mt-2">
          <button
            className="bg-red-500 text-white px-4 py-2 rounded mr-2"
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await onDeleteGuru(guru.id);
                toast.success(`ลบ ${guru.name} สำเร็จ`);
                const updatedGurus = await getGuru();
                setGurus(updatedGurus);
                setSelectedGuru(null); // ล้าง Guru ที่เลือกหลังจากลบ
              } catch (error) {
                console.error('Error deleting guru:', error);
                toast.error('ไม่สามารถลบ Guru ได้');
              }
            }}
          >
            ใช่
          </button>
          <button
            className="bg-gray-500 text-white px-4 py-2 rounded"
            onClick={() => toast.dismiss(t.id)}
          >
            ยกเลิก
          </button>
        </div>
      </div>
    ));
  };

  return open ? (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={() => {
        onClose();
        setSelectedGuru(null);
      }}
    >
      <div
        className="p-6 rounded-lg w-[600px] max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--component-bg-color)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4">จัดการ Guru</h2>

        <div className="flex">
          {/* ฝั่งซ้าย: รายชื่อ Guru */}
          <div className="w-1/2 border-r pr-4">
            <div className="mb-4">
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                style={{
                  color: 'var(--text-color)',
                  backgroundColor: 'transparent',
                  borderColor: 'var(--text-color)',
                }}
                placeholder="กรอกชื่อ Guru..."
                value={newGuruName}
                onChange={(e) => setNewGuruName(e.target.value)}
              />
              <button
                className="bg-green-500 text-white px-4 py-2 rounded mt-2 w-full"
                onClick={handleAddGuru}
                disabled={loadingAddGuru}
              >
                {loadingAddGuru ? 'กำลังเพิ่ม...' : 'เพิ่ม'}
              </button>
            </div>

            <ul className="space-y-2">
              {gurus.map((guru: Guru) => (
                <li
                  key={guru.id}
                  className={`p-2 border rounded flex justify-between items-center ${selectedGuru?.id === guru.id ? 'bg-gray-300' : ''
                    }`}
                  style={{ color: 'var(--text-color)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectGuru(guru);
                  }}
                >
                  <span
                    className="cursor-pointer"

                  >
                    {guru.name}
                  </span>
                  <button
                    className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation(); handleDeleteGuru(guru)
                    }}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ฝั่งขวา: รายการ Portfolio */}
          <div className="w-1/2 pl-4">
            <h3 className="font-bold mb-2">
              Portfolio ของ {selectedGuru?.name || '...'}
            </h3>
            <ul className="space-y-2">
              {selectedGuru?.portfolios.map((portfolio) => (
                <li
                  key={portfolio.id}
                  className="p-2 border rounded flex justify-between items-center"
                >
                  {portfolio.name}
                  <button
                    className="text-red-500 font-bold"
                    onClick={() => handleDeletePortfolio(selectedGuru.id, portfolio.id, portfolio.name)}
                  >
                    ลบ
                  </button>
                </li>
              ))}
            </ul>
            {selectedGuru && showPortfolioInput && (
              <div className="space-y-2 mt-4">
                <div
                  className="flex flex-col border rounded p-2"
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: 'var(--text-color)',
                  }}
                >
                  <input
                    type="text"
                    className="w-full border border-1 bg-transparent text-white outline-none mb-2 px-2 py-1"
                    style={{
                      color: 'var(--text-color)',
                    }}
                    placeholder="ชื่อพอร์ตใหม่..."
                    value={newPortfolioName}
                    onChange={(e) => setNewPortfolioName(e.target.value)}
                  />
                  <div className="flex justify-end space-x-2">
                    <button
                      className="bg-gray-500 text-white px-2 py-1 rounded"
                      onClick={() => setShowPortfolioInput(false)} // ปิด TextInput
                    >
                      ยกเลิก
                    </button>
                    <button
                      className="bg-blue-500 text-white px-2 py-1 rounded"
                      onClick={handleAddPortfolio}
                    >
                      ยืนยัน
                    </button>
                  </div>
                </div>
              </div>
            )}
            {selectedGuru && (
              <button className="bg-blue-500 text-white px-4 py-2 rounded mt-4 w-full"
                onClick={() => setShowPortfolioInput(true)}
              >
                + เพิ่มพอร์ต
              </button>
            )}
          </div>

        </div>

        <div className="flex justify-end mt-4">
          <button
            className="bg-gray-500 text-white px-4 py-2 rounded"
            onClick={onClose}
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  ) : null;
};

export default GuruModal;